import {
    createSelector as createSelectorInternal,
    Selector,
    OutputSelector,
    ParametricSelector,
    OutputParametricSelector,
} from 'reselect';
import { getLogger } from '../framework/logging/logger';

const threeSecondsInMilliseconds = 3000;
const allSelectorsKey = '__ALL_SELECTORS__';
const monitoredSelectors: string[] = ['getSearchEntries'];
let insideTelemetry = false;

const individualCallThreshold = 100;
const cumulativeCallsThreshold = 300;
type TelemetryType = 'ExpensiveSelector' | 'MonitoredSelector';
type TelemetryLogProperties = {
    windowDuration: number;
    selectorName: string;
    evaluations: number;
    callCount: number;
    lastCallDuration: number;
    cumulativeDurationForThisSelector: number;
    cumulativeDurationForAllSelectors: number;
};

const selectorInvocationHistory: {
    [selectorName: string]: {
        timestamp: number;
        duration: number;
        alerted?: boolean;
        evaluated: boolean;
    }[];
} = {};

const callSelectorWithTelemetry = <T>(
    selector: OutputSelector<{}, {}, {}>,
    selectorDelegate: () => T,
    selectorName: string
) => {
    // Keep track of whether we are already instrumenting, so we don't double-count nested selectors.
    const wasInsideTelemetry = insideTelemetry;
    insideTelemetry = true;

    // Snapshot before selector is called
    const timeBefore = Date.now();
    const countBefore = selector.recomputations();

    const result = selectorDelegate();

    // Snapshot after selector is called
    const timeAfter = Date.now();
    const countAfter = selector.recomputations();

    const previousCallsForThisSelector = selectorInvocationHistory[selectorName] || [];
    const previousCallsForAllSelectors = selectorInvocationHistory[allSelectorsKey] || [];

    // Trim current selector (and all selectors) log to 3s, and add an entry for the latest execution.
    const duration = timeAfter - timeBefore;
    const isWithinLastThreeSeconds = (log: { timestamp: number }) =>
        log.timestamp > timeAfter - threeSecondsInMilliseconds;
    const logEntry = { timestamp: timeBefore, duration, evaluated: countBefore < countAfter };
    const allCallsInLastThreeSecondsForThisSelector = previousCallsForThisSelector
        .filter(isWithinLastThreeSeconds)
        .concat([logEntry]);
    const allCallsInLastThreeSecondsForAllSelectors = previousCallsForAllSelectors
        .filter(isWithinLastThreeSeconds)
        .concat(wasInsideTelemetry ? [] : [logEntry]);

    // Store the pruned results
    selectorInvocationHistory[selectorName] = allCallsInLastThreeSecondsForThisSelector;
    selectorInvocationHistory[allSelectorsKey] = allCallsInLastThreeSecondsForAllSelectors;

    // Work out if we should log an alert
    const cumulativeDurationForThisSelector = allCallsInLastThreeSecondsForThisSelector.reduce(
        (total, call) => total + call.duration,
        0
    );
    const cumulativeDurationForAllSelectors = allCallsInLastThreeSecondsForAllSelectors.reduce(
        (total, call) => total + call.duration,
        0
    );
    const evaluations = allCallsInLastThreeSecondsForThisSelector.reduce(
        (total, call) => (call.evaluated ? 1 + total : total),
        0
    );
    const alreadyAlerted = allCallsInLastThreeSecondsForThisSelector.some(call => call.alerted);
    let logType: TelemetryType = undefined;

    if (!alreadyAlerted) {
        if (
            duration >= individualCallThreshold ||
            cumulativeDurationForThisSelector >= cumulativeCallsThreshold
        ) {
            logType = 'ExpensiveSelector';
        } else if (
            monitoredSelectors.some(monitoredSelector => selectorName === monitoredSelector)
        ) {
            logType = 'MonitoredSelector';
        }

        if (logType) {
            const properties = {
                windowDuration: threeSecondsInMilliseconds,
                selectorName,
                evaluations,
                callCount: allCallsInLastThreeSecondsForThisSelector.length,
                lastCallDuration: duration,
                cumulativeDurationForThisSelector,
                cumulativeDurationForAllSelectors,
            };
            logSelectorTelemetry(logType, properties);
            allCallsInLastThreeSecondsForThisSelector[
                allCallsInLastThreeSecondsForThisSelector.length - 1
            ].alerted = true;
        }
    }

    // Flag that we caused an alert to avoid spam from the same selector

    insideTelemetry = false;
    return result;
};

const logSelectorTelemetry = (
    telemetryType: TelemetryType,
    logProperties: TelemetryLogProperties
) => {
    const logger = getLogger();
    if (logger) {
        logger.log(telemetryType, logProperties);
    }
};

// The following are a whole bunch of strongly typed function overloads for the generic solution at the bottom of the page:
// Fourteen Selectors
export function createSelector<
    S,
    P,
    R1,
    R2,
    R3,
    R4,
    R5,
    R6,
    R7,
    R8,
    R9,
    R10,
    R11,
    R12,
    R13,
    R14,
    T
>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>,
        ParametricSelector<S, P, R6>,
        ParametricSelector<S, P, R7>,
        ParametricSelector<S, P, R8>,
        ParametricSelector<S, P, R9>,
        ParametricSelector<S, P, R10>,
        ParametricSelector<S, P, R11>,
        ParametricSelector<S, P, R12>,
        ParametricSelector<S, P, R13>,
        ParametricSelector<S, P, R14>
    ],
    combiner: (
        res1: R1,
        res2: R2,
        res3: R3,
        res4: R4,
        res5: R5,
        res6: R6,
        res7: R7,
        res8: R8,
        res9: R9,
        res10: R10,
        res11: R11,
        res12: R12,
        res13: R13,
        res14: R14
    ) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

// The following are a whole bunch of strongly typed function overloads for the generic solution at the bottom of the page:
// Thirteen Selectors
export function createSelector<S, P, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10, R11, R12, R13, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>,
        ParametricSelector<S, P, R6>,
        ParametricSelector<S, P, R7>,
        ParametricSelector<S, P, R8>,
        ParametricSelector<S, P, R9>,
        ParametricSelector<S, P, R10>,
        ParametricSelector<S, P, R11>,
        ParametricSelector<S, P, R12>,
        ParametricSelector<S, P, R13>
    ],
    combiner: (
        res1: R1,
        res2: R2,
        res3: R3,
        res4: R4,
        res5: R5,
        res6: R6,
        res7: R7,
        res8: R8,
        res9: R9,
        res10: R10,
        res11: R11,
        res12: R12,
        res13: R13
    ) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

// Twelve Selectors
export function createSelector<S, P, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10, R11, R12, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>,
        ParametricSelector<S, P, R6>,
        ParametricSelector<S, P, R7>,
        ParametricSelector<S, P, R8>,
        ParametricSelector<S, P, R9>,
        ParametricSelector<S, P, R10>,
        ParametricSelector<S, P, R11>,
        ParametricSelector<S, P, R12>
    ],
    combiner: (
        res1: R1,
        res2: R2,
        res3: R3,
        res4: R4,
        res5: R5,
        res6: R6,
        res7: R7,
        res8: R8,
        res9: R9,
        res10: R10,
        res11: R11,
        res12: R12
    ) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

// Eleven Selectors
export function createSelector<S, P, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10, R11, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>,
        ParametricSelector<S, P, R6>,
        ParametricSelector<S, P, R7>,
        ParametricSelector<S, P, R8>,
        ParametricSelector<S, P, R9>,
        ParametricSelector<S, P, R10>,
        ParametricSelector<S, P, R11>
    ],
    combiner: (
        res1: R1,
        res2: R2,
        res3: R3,
        res4: R4,
        res5: R5,
        res6: R6,
        res7: R7,
        res8: R8,
        res9: R9,
        res10: R10,
        res11: R11
    ) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10, R11, T>(
    selectorName: string,
    selectors: [
        Selector<S, R1>,
        Selector<S, R2>,
        Selector<S, R3>,
        Selector<S, R4>,
        Selector<S, R5>,
        Selector<S, R6>,
        Selector<S, R7>,
        Selector<S, R8>,
        Selector<S, R9>,
        Selector<S, R10>,
        Selector<S, R11>
    ],
    combiner: (
        res1: R1,
        res2: R2,
        res3: R3,
        res4: R4,
        res5: R5,
        res6: R6,
        res7: R7,
        res8: R8,
        res9: R9,
        res10: R10,
        res11: R11
    ) => T
): OutputSelector<S, T, typeof combiner>;

// Ten Selectors
export function createSelector<S, P, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>,
        ParametricSelector<S, P, R6>,
        ParametricSelector<S, P, R7>,
        ParametricSelector<S, P, R8>,
        ParametricSelector<S, P, R9>,
        ParametricSelector<S, P, R10>
    ],
    combiner: (
        res1: R1,
        res2: R2,
        res3: R3,
        res4: R4,
        res5: R5,
        res6: R6,
        res7: R7,
        res8: R8,
        res9: R9,
        res10: R10
    ) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10, T>(
    selectorName: string,
    selectors: [
        Selector<S, R1>,
        Selector<S, R2>,
        Selector<S, R3>,
        Selector<S, R4>,
        Selector<S, R5>,
        Selector<S, R6>,
        Selector<S, R7>,
        Selector<S, R8>,
        Selector<S, R9>,
        Selector<S, R10>
    ],
    combiner: (
        res1: R1,
        res2: R2,
        res3: R3,
        res4: R4,
        res5: R5,
        res6: R6,
        res7: R7,
        res8: R8,
        res9: R9,
        res10: R10
    ) => T
): OutputSelector<S, T, typeof combiner>;

// Nine Selectors
export function createSelector<S, P, R1, R2, R3, R4, R5, R6, R7, R8, R9, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>,
        ParametricSelector<S, P, R6>,
        ParametricSelector<S, P, R7>,
        ParametricSelector<S, P, R8>,
        ParametricSelector<S, P, R9>
    ],
    combiner: (
        res1: R1,
        res2: R2,
        res3: R3,
        res4: R4,
        res5: R5,
        res6: R6,
        res7: R7,
        res8: R8,
        res9: R9
    ) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, R3, R4, R5, R6, R7, R8, R9, T>(
    selectorName: string,
    selectors: [
        Selector<S, R1>,
        Selector<S, R2>,
        Selector<S, R3>,
        Selector<S, R4>,
        Selector<S, R5>,
        Selector<S, R6>,
        Selector<S, R7>,
        Selector<S, R8>,
        Selector<S, R9>
    ],
    combiner: (
        res1: R1,
        res2: R2,
        res3: R3,
        res4: R4,
        res5: R5,
        res6: R6,
        res7: R7,
        res8: R8,
        res9: R9
    ) => T
): OutputSelector<S, T, typeof combiner>;

// Eight Selectors
export function createSelector<S, P, R1, R2, R3, R4, R5, R6, R7, R8, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>,
        ParametricSelector<S, P, R6>,
        ParametricSelector<S, P, R7>,
        ParametricSelector<S, P, R8>
    ],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4, res5: R5, res6: R6, res7: R7, res8: R8) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, R3, R4, R5, R6, R7, R8, T>(
    selectorName: string,
    selectors: [
        Selector<S, R1>,
        Selector<S, R2>,
        Selector<S, R3>,
        Selector<S, R4>,
        Selector<S, R5>,
        Selector<S, R6>,
        Selector<S, R7>,
        Selector<S, R8>
    ],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4, res5: R5, res6: R6, res7: R7, res8: R8) => T
): OutputSelector<S, T, typeof combiner>;

// Seven Selectors
export function createSelector<S, P, R1, R2, R3, R4, R5, R6, R7, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>,
        ParametricSelector<S, P, R6>,
        ParametricSelector<S, P, R7>
    ],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4, res5: R5, res6: R6, res7: R7) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, R3, R4, R5, R6, R7, T>(
    selectorName: string,
    selectors: [
        Selector<S, R1>,
        Selector<S, R2>,
        Selector<S, R3>,
        Selector<S, R4>,
        Selector<S, R5>,
        Selector<S, R6>,
        Selector<S, R7>
    ],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4, res5: R5, res6: R6, res7: R7) => T
): OutputSelector<S, T, typeof combiner>;

// Six Selectors
export function createSelector<S, P, R1, R2, R3, R4, R5, R6, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>,
        ParametricSelector<S, P, R6>
    ],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4, res5: R5, res6: R6) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, R3, R4, R5, R6, T>(
    selectorName: string,
    selectors: [
        Selector<S, R1>,
        Selector<S, R2>,
        Selector<S, R3>,
        Selector<S, R4>,
        Selector<S, R5>,
        Selector<S, R6>
    ],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4, res5: R5, res6: R6) => T
): OutputSelector<S, T, typeof combiner>;

// Five Selectors
export function createSelector<S, P, R1, R2, R3, R4, R5, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>,
        ParametricSelector<S, P, R5>
    ],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4, res5: R5) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, R3, R4, R5, T>(
    selectorName: string,
    selectors: [
        Selector<S, R1>,
        Selector<S, R2>,
        Selector<S, R3>,
        Selector<S, R4>,
        Selector<S, R5>
    ],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4, res5: R5) => T
): OutputSelector<S, T, typeof combiner>;

// Four Selectors
export function createSelector<S, P, R1, R2, R3, R4, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>,
        ParametricSelector<S, P, R4>
    ],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, R3, R4, T>(
    selectorName: string,
    selectors: [Selector<S, R1>, Selector<S, R2>, Selector<S, R3>, Selector<S, R4>],
    combiner: (res1: R1, res2: R2, res3: R3, res4: R4) => T
): OutputSelector<S, T, typeof combiner>;

// Three Selectors
export function createSelector<S, P, R1, R2, R3, T>(
    selectorName: string,
    selectors: [
        ParametricSelector<S, P, R1>,
        ParametricSelector<S, P, R2>,
        ParametricSelector<S, P, R3>
    ],
    combiner: (res1: R1, res2: R2, res3: R3) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, R3, T>(
    selectorName: string,
    selectors: [Selector<S, R1>, Selector<S, R2>, Selector<S, R3>],
    combiner: (res1: R1, res2: R2, res3: R3) => T
): OutputSelector<S, T, typeof combiner>;

// Two Selectors
export function createSelector<S, P, R1, R2, T>(
    selectorName: string,
    selectors: [ParametricSelector<S, P, R1>, ParametricSelector<S, P, R2>],
    combiner: (res1: R1, res2: R2) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, R2, T>(
    selectorName: string,
    selectors: [Selector<S, R1>, Selector<S, R2>],
    combiner: (res1: R1, res2: R2) => T
): OutputSelector<S, T, typeof combiner>;

// One Selector
export function createSelector<S, P, R1, T>(
    selectorName: string,
    selectors: [ParametricSelector<S, P, R1>],
    combiner: (res: R1) => T
): OutputParametricSelector<S, P, T, typeof combiner>;

export function createSelector<S, R1, T>(
    selectorName: string,
    selectors: [Selector<S, R1>],
    combiner: (res: R1) => T
): OutputSelector<S, T, typeof combiner>;

// Generic Implementation
export function createSelector(
    selectorName: string,
    selectors: any,
    combiner: (...res: any[]) => any
): any {
    const createdSelector = createSelectorInternal(selectors, combiner);
    const newEvaluationFunction = (...args) =>
        callSelectorWithTelemetry(
            createdSelector,
            () => (createdSelector as any)(...args),
            selectorName
        );
    return Object.assign(newEvaluationFunction, createdSelector) as typeof createdSelector;
}
